import { useEffect, useId, useRef, type ComponentPropsWithRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "../lib/cn";
import { useIsMobile } from "../lib/use-is-mobile";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";

export interface DialogProps extends Omit<ComponentPropsWithRef<"dialog">, "title" | "onClose"> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Boutons d'action posés en pied de modale. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Empêche la fermeture par Échap et par clic sur le voile. Réservé aux actions non annulables. */
  dismissible?: boolean;
}

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
} as const;

/**
 * Fenêtre modale responsive.
 * Sur mobile (< 640px), s'affiche sous forme de BottomSheet Vaul 100% swipable.
 * Sur desktop (>= 640px), utilise l'élément `<dialog>` natif du navigateur.
 *
 * ── Pourquoi la modale est portalisée dans `<body>` ──────────────────────────
 * Une modale native est peinte dans la TOP LAYER, une couche que le navigateur
 * pose au-dessus de tout le document. On la croit donc à l'abri de son contexte
 * de rendu. C'est faux sur un point précis : si un ANCÊTRE porte `transform`,
 * `filter`, `perspective`, `backdrop-filter` ou `contain: paint`, cet ancêtre
 * devient le BLOC CONTENEUR des descendants en position fixe — top layer
 * comprise. La modale cesse alors d'être calée sur la fenêtre : elle se
 * recentre dans la carte qui la contient, défile avec elle, et son voile ne
 * couvre plus que ce morceau de page.
 *
 * Le design system pose `transform-gpu`, `will-change` et `contain-*` un peu
 * partout pour garder les animations sur le compositeur — la condition est donc
 * remplie en permanence. Portaliser dans `<body>` supprime la cause plutôt que
 * d'interdire ces utilitaires : la modale n'a plus d'ancêtre du tout.
 */
export function Dialog({
  className,
  open,
  onClose,
  title,
  description,
  footer,
  size = "md",
  dismissible = true,
  children,
  ...props
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const isMobile = useIsMobile();

  // On synchronise l'état React avec la méthode impérative du navigateur sur desktop.
  useEffect(() => {
    if (isMobile) return;
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open, isMobile]);

  // Si sur mobile : rendu automatique sous forme de Vrai BottomSheet Vaul swipable
  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        title={title}
        description={description}
        footer={footer}
        dismissible={dismissible}
        className={className}
      >
        {children}
      </BottomSheet>
    );
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <dialog
      ref={ref}
      data-nexty-dialog=""
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(event) => {
        if (dismissible && event.target === ref.current) onClose();
      }}
      className={cn(
        "m-auto w-[calc(100%-2rem)] rounded-xl border border-border-strong/30 bg-surface-raised p-0 text-fg shadow-overlay inset-shadow-raised",
        "backdrop:bg-surface-overlay backdrop:backdrop-blur-sm",
        SIZES[size],
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-4 border-b border-border p-5">
        <div className="flex flex-col gap-1">
          <h2 id={titleId} className="text-base font-semibold tracking-tight text-fg">
            {title}
          </h2>
          {description && <p className="text-sm text-fg-muted">{description}</p>}
        </div>

        {dismissible && (
          <Button variant="ghost" size="icon-sm" aria-label="Fermer" onClick={onClose}>
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              aria-hidden="true"
              className="size-4"
            >
              <path d="m5 5 10 10M15 5 5 15" />
            </svg>
          </Button>
        )}
      </div>

      {children && <div className="p-5 text-sm text-fg">{children}</div>}

      {footer && (
        <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-border p-4 bg-surface-sunken/40">
          {footer}
        </div>
      )}
    </dialog>,
    document.body,
  );
}
